import { nCr } from './counting';

export function binomialPmf(n: number, p: number, k: number): number {
  if (k < 0 || k > n) return 0;
  if (n > 1000) {
    const logC = logBinomialCoeff(n, k);
    const logP = k * Math.log(p) + (n - k) * Math.log(1 - p);
    return Math.exp(logC + logP);
  }
  return nCr(n, k) * p ** k * (1 - p) ** (n - k);
}

export function binomialCdf(n: number, p: number, k: number): number {
  if (k < 0) return 0;
  if (k >= n) return 1;
  let acc = 0;
  for (let i = 0; i <= k; i++) acc += binomialPmf(n, p, i);
  return acc;
}

export function geometricPmf(p: number, k: number): number {
  if (k < 1 || !Number.isInteger(k)) return 0;
  return (1 - p) ** (k - 1) * p;
}

export function geometricCdf(p: number, k: number): number {
  if (k < 1) return 0;
  return 1 - (1 - p) ** Math.floor(k);
}

export function poissonPmf(lambda: number, k: number): number {
  if (k < 0 || !Number.isInteger(k)) return 0;
  if (lambda <= 0) return k === 0 ? 1 : 0;
  const logP = -lambda + k * Math.log(lambda) - logFactorial(k);
  return Math.exp(logP);
}

export function poissonCdf(lambda: number, k: number): number {
  if (k < 0) return 0;
  let acc = 0;
  for (let i = 0; i <= k; i++) acc += poissonPmf(lambda, i);
  return acc;
}

export function hypergeometricPmf(N: number, K: number, n: number, k: number): number {
  if (k < 0 || k > n || k > K || n - k > N - K) return 0;
  return (nCr(K, k) * nCr(N - K, n - k)) / nCr(N, n);
}

export interface DistributionMoments {
  mean: number;
  variance: number;
}

export function distributionMoments(
  spec:
    | { kind: 'binomial'; n: number; p: number }
    | { kind: 'geometric'; p: number }
    | { kind: 'poisson'; lambda: number }
    | { kind: 'hypergeometric'; N: number; K: number; n: number },
): DistributionMoments {
  switch (spec.kind) {
    case 'binomial':
      return { mean: spec.n * spec.p, variance: spec.n * spec.p * (1 - spec.p) };
    case 'geometric':
      return { mean: 1 / spec.p, variance: (1 - spec.p) / (spec.p * spec.p) };
    case 'poisson':
      return { mean: spec.lambda, variance: spec.lambda };
    case 'hypergeometric': {
      const { N, K, n } = spec;
      const mean = (n * K) / N;
      const variance = ((n * K * (N - K) * (N - n)) / (N * N)) * (1 / (N - 1));
      return { mean, variance };
    }
  }
}

function logFactorial(n: number): number {
  if (n <= 1) return 0;
  let acc = 0;
  for (let i = 2; i <= n; i++) acc += Math.log(i);
  return acc;
}

function logBinomialCoeff(n: number, k: number): number {
  return logFactorial(n) - logFactorial(k) - logFactorial(n - k);
}
